// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import StyledWrapper from '@insite/client-framework/Common/StyledWrapper';
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductsDataView,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from '@insite/client-framework/Translate';
import { ProductModel, ProductPriceDto } from '@insite/client-framework/Types/ApiModels';
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGAUpdate } from "@plumberssupply/App/Handlers/GoogleAnalytics/GA/xGA";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getDataLayer } from "@plumberssupply/App/Utilities/DataLayer";
import color from '@plumberssupply/XDL/Utilities/color';
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type ProductData = Pick<ProductModel, "productNumber" | "productTitle" | "brand" | "properties"> & { pricing: ProductPriceDto | undefined }

const mapStateToProps = (state: CustomAppState) => {
    let PDPProduct: ProductData | null = null;
    let PLPProducts: ProductData[] = [];
    const session = getSession(state);

    const currentPage = getCurrentPage(state).type;
    const location = getLocation(state);
    const isPDP = currentPage === "ProductDetailsPage";
    const isPLP = currentPage === "ProductListPage";
    const isCartPage = currentPage === "CartPage";

    if (isPDP) {
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath);
        const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
        const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
        const productInfo =
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined;

        if (productState?.value) {
            const { productNumber, productTitle, brand, properties } = productState.value
            PDPProduct = {
                productNumber,
                productTitle,
                brand,
                properties,
                pricing: productInfo?.pricing as ProductPriceDto
            };
        }
    }

    if (isPLP) {
        const {
            pages: {
                productList: { parameter },
            },
        } = state;
        const productsDataView = getProductsDataView(state, parameter);
        if (productsDataView?.value) {
            PLPProducts = productsDataView.value.map(product => {
                const { productNumber, productTitle, brand, properties } = product;
                const productInfo = state.pages.productList.productInfosByProductId[product.id];
                return {
                    productNumber,
                    productTitle,
                    brand,
                    properties,
                    pricing: productInfo?.pricing
                }
            });
        }
    }

    return {
        currentPage,
        PDPProduct,
        PLPProducts,
        cart: getCurrentCartState(state),
        xGAState: state.xGA,
        currencyCode: state.context?.session?.currency?.currencyCode,
        isPDP,
        isPLP,
        isCartPage,
        userProfileId: session.userProfileId
    };
};

const mapDispatchToProps = {
    xGAUpdate,
};

const enum fields {
    checkoutFlowEvents = "checkoutFlowEvents",
    addToCartEvent = "addToCartEvent",
    purchaseEvent = "purchaseEvent",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    pdpViewEvent = "pdpViewEvent",
    productImpressionEvent = "productImpressionEvent",
    selectPromotionEvent = "selectPromotionEvent",
    viewCartEvent = "viewCartEvent",
    includeProductBrand = "includeProductBrand",
    includeProductCategory = "includeProductCategory",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkoutFlowEvents]: boolean;
        [fields.addToCartEvent]: boolean;
        [fields.purchaseEvent]: boolean;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.pdpViewEvent]: boolean;
        [fields.productImpressionEvent]: boolean;
        [fields.selectPromotionEvent]: boolean;
        [fields.viewCartEvent]: boolean;
        [fields.includeProductBrand]: boolean;
        [fields.includeProductCategory]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface GoogleAnalyticsWidgetStyles {
    wrapper?: InjectableCss;
    instructionText?: TypographyPresentationProps;
}

export const googleAnalyticsWidgetStyles: GoogleAnalyticsWidgetStyles = {
    wrapper: {
        css: css`
            padding: 10px;
            background-color: ${color("secondary.main")};
        `,
    },
    instructionText: {
        color: "primary.contrast",
        variant: "h6",
        css: css`
            margin: 0;
        `,
    },
};

const GoogleAnalyticsWidget: FC<Props> = ({
    xGAUpdate,
    fields,
    currentPage,
    PDPProduct,
    PLPProducts,
    cart,
    xGAState,
    currencyCode,
    shellContext,
    isPDP,
    isPLP,
    isCartPage,
    userProfileId
}) => {
    const styles = googleAnalyticsWidgetStyles;
    if (shellContext.isInShell && shellContext.isEditing) {
        // Placeholder in CMS view
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.instructionText}>
                    {translate("Google Analytics Widget (GA4)")}
                </Typography>
            </StyledWrapper>
        );
    }
    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);

    useEffect(() => {
        xGAUpdate({
            checkoutFlowEvents: fields["checkoutFlowEvents"] || false,
            addToCartEvent: fields["addToCartEvent"] || false,
            purchaseEvent: fields["purchaseEvent"] || false,
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            pdpViewEvent: fields["pdpViewEvent"] || false,
            productImpressionEvent: fields["productImpressionEvent"] || false,
            selectPromotionEvent: fields["selectPromotionEvent"] || false,
            viewCartEvent: fields["viewCartEvent"] || false,
            includeProductBrand: fields["includeProductBrand"] || false,
            includeProductCategory: fields["includeProductCategory"] || false,
        });
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer) {
            if (isPDP && xGAState.pdpViewEvent && PDPProduct && !PDPInfoLoaded) {
                setPDPInfoLoaded(true);
                const googleAnalyticsData = {
                    event: "view_item",
                    ...(userProfileId !== null ? {authenticated: true, userId:userProfileId }:{authenticated:false}),
                    items: [
                        {
                            item_name: PDPProduct?.productTitle,
                            item_id: PDPProduct?.productNumber,
                            price: PDPProduct.pricing?.unitNetPrice,
                            item_list_name: "Product Detail Page",
                            item_brand: xGAState["includeProductBrand"]
                                ? PDPProduct.brand?.name || ""
                                : undefined,
                            item_category: xGAState["includeProductCategory"]
                                ? PDPProduct.properties?.["category"] || ""
                                : undefined,
                            item_list_id: xGAState["includeProductCategory"]
                                ? PDPProduct.properties?.["categoryId"] || ""
                                : undefined,
                        },
                    ],
                };

                dataLayer.push(googleAnalyticsData);
            }

            if (isPLP && xGAState.productImpressionEvent && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);
                const googleAnalyticsData = {
                    event: "view_item_list",
                    items: PLPProducts.map((product, idx) => (
                        {
                            item_name: product?.productTitle,
                            item_id: product?.productNumber,
                            price: product?.pricing?.unitNetPrice,
                            index: idx,
                            item_brand: xGAState["includeProductBrand"]
                                ? product?.brand?.name || ""
                                : undefined,
                            item_category: xGAState["includeProductCategory"]
                                ? product?.properties?.["category"] || ""
                                : undefined,
                            item_list_id: xGAState["includeProductCategory"]
                                ? product?.properties?.["categoryId"] || ""
                                : undefined,
                        }
                    ))
                };

                dataLayer.push(googleAnalyticsData);
            }
        }
    }, [PLPProducts, PDPProduct, currentPage, xGAState]);


    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart || !cart.value || cart.isLoading) {
            return;
        }

        if (isCartPage && xGAState.viewCartEvent) {
            setPDPInfoLoaded(false);
            setPLPInfoLoaded(false);

            const googleAnalyticsData = {
                event: "view_cart",
                value: cart.value.orderSubTotal,
                currency: currencyCode,                
                items: cart.value?.cartLines?.map((cartItem, idx) => (
                    {
                        item_name: cartItem.productName,
                        item_id: cartItem.productId,
                        price: cartItem.pricing?.unitNetPrice,
                        quantity: cartItem.qtyOrdered,
                        index: idx,
                        item_brand: xGAState["includeProductBrand"]
                            ? cartItem.brand?.name || ""
                            : undefined,
                        item_category: xGAState["includeProductCategory"]
                            ? cartItem.properties?.["category"] || ""
                            : undefined,
                        item_list_id: xGAState["includeProductCategory"]
                            ? cartItem.properties?.["categoryId"] || ""
                            : undefined,
                    }
                )),
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, cart]);

    return null;
};

const eventsTab = {
    displayName: "Events",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(GoogleAnalyticsWidget)),
    definition: {
        displayName: "Google Analytics Management (GA4)",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove Cartline (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.viewCartEvent,
                displayName: "View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.purchaseEvent,
                displayName: "Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.checkoutFlowEvents,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.selectPromotionEvent,
                displayName: "Select Promotion Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: eventsTab,
            },
            {
                name: fields.includeProductBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeProductCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
